import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { extractApiError } from '~/utils/extract-api-error';

import { getExportStatus, refreshExportDownloadUrl } from '../services/export.service';
import type { ExportStatusFile } from '../types/export.types';

/** True while a per-file signed URL is present and not past its expiry. */
function isUsableUrl(file: ExportStatusFile): file is ExportStatusFile & { downloadUrl: string } {
  if (!file.downloadUrl) return false;
  if (!file.downloadUrlExpiresAt) return true;
  return Date.parse(file.downloadUrlExpiresAt) > Date.now();
}

/**
 * Download a completed export's CSV in-app. The `downloadUrl` is a pre-signed
 * public GCS URL served with `Content-Disposition: attachment` — no auth header
 * needed — so we trigger the download via a `download`-flagged anchor rather
 * than round-tripping through Ky/blob (the GCS bucket has no CORS grant for our
 * origin, so a `fetch()` would fail). Prefer an already-minted URL (from the SSE
 * terminal frame); otherwise call the refresh endpoint to mint a fresh one.
 */
export function useExportDownload() {
  const { t } = useTranslation('exports');
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Trigger a file download via a transient anchor — no new tab/window. The
   * `download` attribute signals download intent; the actual filename comes from
   * the URL's `Content-Disposition` (the attribute itself is ignored for
   * cross-origin GCS URLs, but the attachment disposition still forces a save).
   */
  const openUrl = useCallback((url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  /**
   * Download an export. Uses `knownUrl` when supplied and not known-expired;
   * otherwise mints a fresh signed URL via `POST /{id}/downloadUrl:refresh`.
   * Resolves `true` once the browser download was triggered, `false` on error,
   * so callers can surface a "downloaded" state only when it actually fired.
   */
  const download = useCallback(
    async (exportId: string, knownUrl?: string | null): Promise<boolean> => {
      if (knownUrl) {
        openUrl(knownUrl);
        return true;
      }
      setIsRefreshing(true);
      try {
        const { downloadUrl } = await refreshExportDownloadUrl(exportId);
        openUrl(downloadUrl);
        return true;
      } catch (err) {
        toast.error(extractApiError(err, t('toast.downloadError')));
        return false;
      } finally {
        setIsRefreshing(false);
      }
    },
    [openUrl, t],
  );

  /**
   * Download one file of a multi-file (transformed) export. Uses the file's own
   * signed URL from `files[]`; when it is missing or expired, re-reads the status,
   * which signs every file afresh (the refresh endpoint only mints the first file).
   */
  const downloadFile = useCallback(
    async (exportId: string, file: ExportStatusFile): Promise<boolean> => {
      if (isUsableUrl(file)) {
        openUrl(file.downloadUrl);
        return true;
      }
      setIsRefreshing(true);
      try {
        const status = await getExportStatus(exportId);
        const fresh = (status.files ?? []).find(
          (f) => f.fileName === file.fileName && f.subfolder === file.subfolder,
        );
        if (!fresh?.downloadUrl) throw new Error(t('toast.downloadError'));
        openUrl(fresh.downloadUrl);
        return true;
      } catch (err) {
        toast.error(extractApiError(err, t('toast.downloadError')));
        return false;
      } finally {
        setIsRefreshing(false);
      }
    },
    [openUrl, t],
  );

  return { download, downloadFile, isRefreshing };
}
